import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import Icon from "./Icon";

// C3 — Buscador global (Spotlight). Encuentra tareas y proyectos por
// título/código/descripción y por los campos personalizados marcados como
// indexables. Se usa en línea en la cabecera y muestra resultados anclados debajo.
export default function Spotlight({ open, onClose, compact = false }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ tasks: [], projects: [] });
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(0);
  const [active, setActive] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const timer = useRef(null);
  const visible = open || active;

  const close = () => { setActive(false); onClose(); };

  useEffect(() => {
    if (open) { setQ(""); setRes({ tasks: [], projects: [] }); setSel(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  useEffect(() => {
    if (!visible) return undefined;
    const outside = (event) => { if (!rootRef.current?.contains(event.target)) close(); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [visible]);

  // Búsqueda con debounce (300 ms). Mínimo 2 caracteres.
  useEffect(() => {
    clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setRes({ tasks: [], projects: [] }); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get("/search", { params: { q: term } })
        .then((r) => { setRes(r.data || { tasks: [], projects: [] }); setSel(0); })
        .catch(() => setRes({ tasks: [], projects: [] }))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  // Grupos del buscador global (en el orden en que se pintan). Cada grupo dice
  // qué clave de la respuesta usa, su etiqueta/icono, a dónde navega y cómo
  // mostrar título y subtítulo de cada ítem.
  const GRUPOS = [
    { key: "projects", label: "Proyectos", icon: "folder", color: "text-brand-500",
      to: (x) => `/proyectos?p=${x.id}`, tit: (x) => x.name,
      sub: (x) => `${x.country ? `${x.country} · ` : ""}coincide en ${x.match_field}${x.snippet ? ` — ${x.snippet}` : ""}` },
    { key: "tasks", label: "Tareas", icon: "check", color: "text-slate-400",
      to: (x) => `/proyectos?task=${x.id}`, tit: (x) => `${x.code ? `${x.code} ` : ""}${x.title}`,
      sub: (x) => `${x.project_name ? `${x.project_name} · ` : ""}coincide en ${x.match_field}` },
    { key: "requisiciones", label: "Requisiciones", icon: "list", color: "text-slate-400",
      to: (x) => `/compras/requisiciones/${x.id}`, tit: (x) => x.numero,
      sub: (x) => `${x.estado || ""}${x.snippet ? ` — ${x.snippet}` : ""}` },
    { key: "facturas", label: "Facturas", icon: "list", color: "text-slate-400",
      to: (x) => `/compras/facturas/${x.id}`, tit: (x) => x.numero,
      sub: (x) => `${x.proveedor || ""}${x.estado ? ` · ${x.estado}` : ""}` },
    { key: "activos", label: "Activos (ITAM)", icon: "list", color: "text-slate-400",
      to: (x) => `/itam/activos/${x.id}`, tit: (x) => x.etiqueta,
      sub: (x) => (x.serie ? `Serie ${x.serie}` : "") },
    { key: "proveedores", label: "Proveedores", icon: "users", color: "text-slate-400",
      to: () => "/compras/catalogos", tit: (x) => x.nombre, sub: (x) => x.ruc || "" },
    { key: "usuarios", label: "Usuarios", icon: "users", color: "text-slate-400",
      to: () => "/admin/usuarios", tit: (x) => x.nombre, sub: (x) => x.email || "" },
    { key: "etiquetas", label: "País / Marca", icon: "star", color: "text-slate-400",
      to: () => "/admin/etiquetas", tit: (x) => x.nombre, sub: (x) => (x.tipo === "pais" ? "País" : "Marca") },
  ];

  // Lista plana en el mismo orden que se pintan, para navegar con teclado.
  const flat = useMemo(
    () => GRUPOS.flatMap((g) => (res[g.key] || []).map((it) => ({ g, it }))),
    [res],
  );

  const go = (entry) => {
    if (!entry) return;
    close();
    nav(entry.g.to(entry.it));
  };

  const onKey = (e) => {
    if (e.key === "Escape") { close(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[sel]); }
  };

  const term = q.trim();
  const empty = term.length >= 2 && !loading && flat.length === 0;
  const input = (
    <div className={`flex items-center gap-2 ${compact ? "border-b border-slate-100 px-3" : "h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 transition focus-within:border-brand-400 focus-within:bg-white"}`}>
      <Icon name="search" size={17} className="shrink-0 text-slate-500" />
      <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
        onFocus={() => setActive(true)}
        placeholder="Buscar tareas, proyectos, personas…"
        className={`min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 focus-visible:ring-0 ${compact ? "py-3.5" : "py-2"}`} />
      {loading && <Icon name="clock" size={15} className="animate-spin text-slate-300" />}
      {compact && <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-400 sm:block">Esc</kbd>}
    </div>
  );

  const results = (
    <>
      <div className="max-h-[52vh] overflow-y-auto">
        {term.length < 2 && (
          <p className="px-4 py-6 text-center text-xs text-slate-400">Escribe al menos 2 caracteres. También busca dentro de los campos personalizados marcados como indexables.</p>
        )}
        {empty && <p className="px-4 py-6 text-center text-sm text-slate-400">Sin resultados para «{term}».</p>}

        {(() => {
          let base = 0;   // índice global acumulado para resaltar con teclado
          return GRUPOS.map((g) => {
            const items = res[g.key] || [];
            if (!items.length) return null;
            const start = base;
            base += items.length;
            return (
              <div key={g.key}>
                <div className="bg-slate-50 px-4 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">{g.label}</div>
                {items.map((it, i) => {
                  const idx = start + i;
                  const sub = g.sub(it);
                  return (
                    <button key={`${g.key}${it.id}`} onMouseEnter={() => setSel(idx)} onClick={() => go({ g, it })}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left ${sel === idx ? "bg-brand-50" : "hover:bg-slate-50"}`}>
                      <Icon name={g.icon} size={16} className={`shrink-0 ${g.color}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium uppercase text-slate-700">{g.tit(it)}</span>
                        {sub && <span className="block truncate text-[11px] text-slate-400">{sub}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          });
        })()}
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-1.5 text-[11px] text-slate-400">
        <span><kbd className="font-mono">↑↓</kbd> navegar · <kbd className="font-mono">Enter</kbd> abrir</span>
        <span>Buscador global</span>
      </div>
    </>
  );

  return (
    <div ref={rootRef} className={`relative ${compact ? "shrink-0" : "w-full max-w-[470px] min-w-0 flex-1"}`}>
      {compact ? (
        visible ? <div className="absolute right-0 top-full z-[70] mt-2 w-[min(92vw,36rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          {input}{results}
        </div> : (
          <button onClick={() => { setActive(true); setTimeout(() => inputRef.current?.focus(), 0); }} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Buscar (Ctrl/Cmd + K)" aria-label="Buscar">
            <Icon name="search" size={19} />
          </button>
        )
      ) : (
        <>
          {input}
          {visible && <div className="absolute left-0 top-full z-[70] mt-2 w-[min(92vw,36rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">{results}</div>}
        </>
      )}
    </div>
  );
}
